import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Link2, Copy, Trash2, Plus, QrCode } from "lucide-react";

const CHANNELS = ["WhatsApp group", "WhatsApp status", "Telegram", "Instagram", "X / Twitter", "TikTok", "Facebook", "Google ads", "Flyer / offline", "Other"];

export const CampaignLinksCard = () => {
  const [rows, setRows] = useState(null);
  const [name, setName] = useState("");
  const [channel, setChannel] = useState(CHANNELS[0]);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/campaigns").then(({ data }) => setRows(data.campaigns)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    if (name.trim().length < 2) return;
    setBusy(true);
    try {
      const { data } = await api.post("/campaigns", { name: name.trim(), channel });
      navigator.clipboard.writeText(data.link).catch(() => {});
      toast.success("Tracked link created & copied — paste it in the channel");
      setName("");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (code) => {
    try { await api.delete(`/campaigns/${code}`); load(); } catch { toast.error("Could not delete"); }
  };

  return (
    <div className="mt-10 rounded-xl border border-[#27272A] bg-[#121212] p-6" data-testid="campaign-links-card">
      <div className="flex items-center gap-2">
        <Link2 className="h-4 w-4 text-[#007AFF]" />
        <h2 className="font-bold text-lg">Campaign link tracker</h2>
      </div>
      <p className="mt-2 text-sm text-neutral-400">One tracked link per group/platform — see exactly which channel brings sellers.</p>
      <form onSubmit={create} className="mt-4 flex flex-col sm:flex-row gap-2">
        <Input data-testid="campaign-name-input" value={name} onChange={(e) => setName(e.target.value)} maxLength={80}
          placeholder='e.g. "Lagos Traders WhatsApp group"' className="flex-1 bg-black/40 border-[#27272A]" />
        <select data-testid="campaign-channel-select" value={channel} onChange={(e) => setChannel(e.target.value)}
          className="bg-black/40 border border-[#27272A] rounded-lg px-3 h-10 text-sm sm:w-48">
          {CHANNELS.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <Button type="submit" disabled={busy || name.trim().length < 2} data-testid="campaign-create-btn"
          className="rounded-full bg-white text-black hover:bg-neutral-200 h-10 px-5 font-semibold">
          <Plus className="h-4 w-4 mr-1" /> Create link
        </Button>
      </form>
      <div className="mt-4 space-y-2">
        {rows === null && <div className="text-sm text-neutral-500">Loading…</div>}
        {rows?.length === 0 && <div className="text-sm text-neutral-500">No tracked links yet — create one per WhatsApp group before posting.</div>}
        {rows?.map((r) => (
          <div key={r.code} data-testid={`campaign-row-${r.code}`} className="rounded-lg border border-[#27272A] bg-black/30 px-4 py-3">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-sm font-semibold truncate">{r.name} <span className="text-[10px] uppercase tracking-wider text-neutral-500 border border-[#27272A] rounded-full px-2 py-0.5 ml-1">{r.channel}</span></div>
                <div className="text-[11px] text-neutral-500 font-mono truncate">{r.link}</div>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <Button variant="ghost" size="sm" data-testid={`campaign-copy-${r.code}`} className="h-8 px-2 text-neutral-400 hover:text-white"
                  onClick={() => { navigator.clipboard.writeText(r.link); toast.success("Link copied"); }}>
                  <Copy className="h-3.5 w-3.5" />
                </Button>
                <Button variant="ghost" size="sm" data-testid={`campaign-poster-${r.code}`} className="h-8 px-2 text-neutral-400 hover:text-white"
                  title="Download printable QR poster"
                  onClick={async () => {
                    try {
                      toast.info("Building your QR poster…");
                      const res = await api.get(`/campaigns/poster/${r.code}.png`, { responseType: "blob", timeout: 60000 });
                      const url = URL.createObjectURL(res.data);
                      const a = document.createElement("a");
                      a.href = url;
                      a.download = `vismarks-poster-${r.code}.png`;
                      a.click();
                      URL.revokeObjectURL(url);
                    } catch { toast.error("Could not build poster"); }
                  }}>
                  <QrCode className="h-3.5 w-3.5" />
                </Button>
                <Button variant="ghost" size="sm" data-testid={`campaign-delete-${r.code}`} className="h-8 px-2 text-neutral-500 hover:text-[#FF3B30]"
                  onClick={() => remove(r.code)}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
            <div className="mt-2 flex gap-4 text-xs">
              <span className="text-neutral-400">Clicks <span className="text-white font-bold">{r.clicks}</span></span>
              <span className="text-neutral-400">Seller signups <span className="text-[#FF3B30] font-bold">{r.seller_signups}</span></span>
              <span className="text-neutral-400">Buyer signups <span className="text-white font-bold">{r.buyer_signups}</span></span>
              <span className="text-neutral-400">Paying sellers <span className="text-[#34C759] font-bold">{r.paying_sellers}</span></span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
